import { useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarCheck, Clock } from "lucide-react";
import { bookingUrl, CALENDLY_ORIGIN } from "@/config/booking";
import "./calendly.css";

type CalendlyApi = { initInlineWidget: (o: { url: string; parentElement: HTMLElement; prefill?: Record<string, unknown> }) => void };
const SDK = "https://assets.calendly.com/assets/external/widget.js";
let sdkPromise: Promise<CalendlyApi | null> | null = null;
function loadSdk(): Promise<CalendlyApi | null> {
  const existing = (window as Window & { Calendly?: CalendlyApi }).Calendly;
  if (existing) return Promise.resolve(existing);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise(resolve => {
    const s = document.createElement("script");
    s.src = SDK; s.async = true; s.dataset.calendlySdk = "true";
    s.onload = () => resolve((window as Window & { Calendly?: CalendlyApi }).Calendly ?? null);
    s.onerror = () => { sdkPromise = null; resolve(null); };
    document.body.appendChild(s);
  });
  return sdkPromise;
}

/** Inline Calendly with name/email prefilled through the SDK (never via this site's URL). Booking does not fire Meta Lead. */
export function CalendlyInline({ name, email, title = "Strategy call booking calendar" }: { name: string; email: string; title?: string }) {
  const url = bookingUrl();
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed" | "booked">("loading");
  useEffect(() => {
    if (!url || !host.current) return;
    let alive = true;
    const node = host.current;
    loadSdk().then(api => {
      if (!alive) return;
      if (!api) { setState("failed"); return; }
      node.innerHTML = "";
      api.initInlineWidget({ url, parentElement: node, prefill: { name, email } });
      node.querySelector("iframe")?.setAttribute("title", title);
      setState("ready");
    });
    const onMessage = (e: MessageEvent) => {
      const frame = node.querySelector("iframe");
      if (e.origin !== CALENDLY_ORIGIN || !frame || e.source !== frame.contentWindow) return;
      const d = e.data as { event?: unknown };
      if (d && typeof d === "object" && d.event === "calendly.event_scheduled") setState("booked");
    };
    window.addEventListener("message", onMessage);
    return () => { alive = false; window.removeEventListener("message", onMessage); };
  }, [url, name, email, title]);

  if (!url) return <div className="cal-fallback" role="status"><Clock size={22} aria-hidden="true" /><div><strong>Online scheduling is being set up.</strong><p>Your details are with our team, and we'll email you to arrange your strategy call.</p></div></div>;
  return <div className="cal-inline">
    {state === "booked" && <div className="cal-booked" role="status"><CalendarCheck size={20} aria-hidden="true" /> Your call is booked. Check your inbox for the confirmation.</div>}
    {state === "failed" && <div className="cal-fallback" role="alert"><Clock size={22} aria-hidden="true" /><div><strong>The calendar didn't load.</strong><p>Your details are saved. <a href={url} target="_blank" rel="noopener noreferrer">Open the scheduling page in a new tab <ArrowRight size={14} /></a></p></div></div>}
    <div ref={host} className="cal-frame" aria-busy={state === "loading"} />
    {state !== "failed" && <a className="cal-newtab" href={url} target="_blank" rel="noopener noreferrer">Open calendar in a new tab <ArrowRight size={15} /></a>}
  </div>;
}
